import type {Component, Editor} from 'grapesjs';
import {InsertPosition} from '@Components/email-maker-block-manager/components/add-block-menu/add-block-menu.types';
import {unmountAndRemoveElement} from '@Utils/root-element.util';
import {renderAddElementMenu} from '@EmailMakerReact/lib/add-element-menu-renderer';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getCurrentZoom} from '@EmailMakerReact/lib/email-maker-zoom-library';
import {getAllComponentsInTree} from '@EmailMakerReact/lib/email-maker-component-query-library';

const SMALL_SCREEN_MAX_WIDTH = 768;
const BUTTON_Z_INDEX = '10';
const MENU_Z_INDEX = '1000';

interface CanvasOffset {
  centerLeft: number;
  top: number;
  bottom: number;
}

/** True when every offset coordinate is finite; a detached or zero-size element yields NaN/Infinity. */
function isFiniteCanvasOffset(offset: CanvasOffset): boolean {
  return Number.isFinite(offset.centerLeft) && Number.isFinite(offset.bottom) && Number.isFinite(offset.top);
}

/** Injects top/bottom "+" add-element buttons into the canvas iframe for every HAS_BORDER_CONTROLS component. */
export function initAddElementButtons(editor: Editor): () => void {
  const attachedComponents = new WeakSet<Component>();
  const componentCleanups = new Set<() => void>();
  const pendingRepositionFrames = new Set<number>();

  const getElementOffsetInUnscaledCanvasCoords = (element: HTMLElement, canvasBody: HTMLElement, elementRect?: DOMRect): CanvasOffset => {
    const rect = elementRect ?? element.getBoundingClientRect();
    const canvasRect = canvasBody.getBoundingClientRect();
    const zoom = getCurrentZoom(editor) || 1;
    return {
      centerLeft: (rect.left - canvasRect.left + rect.width / 2) / zoom,
      top: (rect.top - canvasRect.top) / zoom,
      bottom: (rect.bottom - canvasRect.top) / zoom,
    };
  };

  const createAddElementButton = (position: 'top' | 'bottom'): HTMLElement => {
    const button = document.createElement('div');
    button.className = `pmw-add-element-button ${position}`;
    button.setAttribute('data-position', position);
    return button;
  };

  const unmountAndRemoveStaleMenuContainer = (): void => {
    const existingContainer = document.querySelector('.pmw-add-element-menu-container');
    if (existingContainer instanceof HTMLElement) {
      unmountAndRemoveElement(existingContainer);
    }
  };

  const renderAddElementMenuInContainer = (anchorElement: HTMLElement, position: 'above' | 'below'): void => {
    unmountAndRemoveStaleMenuContainer();

    const canvasBody = editor.Canvas.getBody();
    canvasBody?.querySelectorAll('.pmw-add-element-button.-active').forEach((btn) => {
      btn.classList.remove('-active');
    });

    anchorElement.classList.add('-active');

    const frameEl = editor.Canvas.getFrameEl();
    if (!frameEl || !document.body) {
      return;
    }

    const container = document.createElement('div');
    container.className = 'pmw-add-element-menu-container';
    container.style.position = 'absolute';
    container.style.zIndex = MENU_Z_INDEX;

    const rect = anchorElement.getBoundingClientRect();
    const frameRect = frameEl.getBoundingClientRect();
    const top = position === 'above' ? rect.top : rect.bottom;
    container.style.top = `${frameRect.top + top + window.scrollY}px`;
    container.style.left = `${frameRect.left + rect.left + window.scrollX}px`;

    document.body.appendChild(container);

    const originalRemove = container.remove.bind(container);
    container.remove = (): void => {
      anchorElement.classList.remove('-active');
      originalRemove();
    };

    renderAddElementMenu(container, {insertPosition: position === 'above' ? InsertPosition.ABOVE : InsertPosition.BELOW}, editor);
  };

  const positionButton = (button: HTMLElement, offset: CanvasOffset): void => {
    const position = button.getAttribute('data-position');
    button.style.position = 'absolute';
    button.style.left = `${offset.centerLeft}px`;
    button.style.top = `${position === 'top' ? offset.top : offset.bottom}px`;
    button.style.transform = 'translate(-50%, -50%)';
    button.style.zIndex = BUTTON_Z_INDEX;
  };

  const attachAddElementButtons = (element: HTMLElement, component: Component): void => {
    if (attachedComponents.has(component)) {
      return;
    }

    const shouldSkipButtons =
      component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL) ||
      component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW) ||
      component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY);
    if (shouldSkipButtons) {
      return;
    }

    const canvasBody = editor.Canvas.getBody();
    if (!canvasBody) {
      return;
    }

    const elementId = element.id || component.getId();
    if (!element.id) {
      element.id = elementId;
    }

    attachedComponents.add(component);

    canvasBody.querySelectorAll(`[data-target-element-id="${elementId}"]`).forEach((btn) => {
      btn.remove();
    });

    const topButton = createAddElementButton('top');
    const bottomButton = createAddElementButton('bottom');
    topButton.setAttribute('data-target-element-id', elementId);
    bottomButton.setAttribute('data-target-element-id', elementId);
    topButton.style.display = 'none';
    bottomButton.style.display = 'none';
    canvasBody.appendChild(topButton);
    canvasBody.appendChild(bottomButton);

    const updateButtonVisibilityForScreenSize = (): void => {
      // Desktop-only: _hidden keeps buttons off small screens, which use the Plus menu instead.
      const isSmallScreen = window.innerWidth <= SMALL_SCREEN_MAX_WIDTH;
      topButton.classList.toggle('_hidden', isSmallScreen);
      bottomButton.classList.toggle('_hidden', isSmallScreen);
    };

    const updateButtonPositions = (): void => {
      const offset = getElementOffsetInUnscaledCanvasCoords(element, canvasBody);
      if (!isFiniteCanvasOffset(offset)) {
        return;
      }
      positionButton(topButton, offset);
      positionButton(bottomButton, offset);
    };

    const showButtons = (): void => {
      topButton.style.display = 'flex';
      bottomButton.style.display = 'flex';
      updateButtonPositions();
    };

    const hideButtons = (): void => {
      if (component.getId() !== editor.getSelected()?.getId()) {
        topButton.style.display = 'none';
        bottomButton.style.display = 'none';
      }
    };

    const handleButtonMouseEnter = (): void => {
      showButtons();
    };

    const handleButtonMouseLeave = (e: MouseEvent): void => {
      const relatedTarget = e.relatedTarget;
      if (relatedTarget instanceof HTMLElement && (relatedTarget === element || element.contains(relatedTarget))) {
        return;
      }
      hideButtons();
    };

    const handleComponentSelected = (selectedComponent: Component): void => {
      if (selectedComponent.getId() === component.getId()) {
        showButtons();
      }
    };

    const handleComponentDeselected = (deselectedComponent: Component): void => {
      if (deselectedComponent.getId() === component.getId()) {
        hideButtons();
      }
    };

    const handleButtonClick = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      const button = e.currentTarget;
      if (!(button instanceof HTMLElement)) {
        return;
      }
      editor.select(component);
      renderAddElementMenuInContainer(button, button.getAttribute('data-position') === 'top' ? 'above' : 'below');
    };

    const scrollHandler = (): void => {
      updateButtonPositions();
    };

    updateButtonVisibilityForScreenSize();
    updateButtonPositions();

    window.addEventListener('resize', updateButtonVisibilityForScreenSize);
    editor.on('component:selected', handleComponentSelected);
    editor.on('component:deselected', handleComponentDeselected);
    topButton.addEventListener('mouseenter', handleButtonMouseEnter);
    topButton.addEventListener('mouseleave', handleButtonMouseLeave);
    bottomButton.addEventListener('mouseenter', handleButtonMouseEnter);
    bottomButton.addEventListener('mouseleave', handleButtonMouseLeave);
    topButton.addEventListener('click', handleButtonClick);
    bottomButton.addEventListener('click', handleButtonClick);

    if (editor.getSelected() === component) {
      showButtons();
    }

    const observer = new MutationObserver(updateButtonPositions);
    observer.observe(element, {attributes: true, childList: true, subtree: true});
    canvasBody.addEventListener('scroll', scrollHandler, true);

    const cleanup = (): void => {
      topButton.remove();
      bottomButton.remove();
      observer.disconnect();
      canvasBody.removeEventListener('scroll', scrollHandler, true);
      window.removeEventListener('resize', updateButtonVisibilityForScreenSize);
      editor.off('component:selected', handleComponentSelected);
      editor.off('component:deselected', handleComponentDeselected);
      attachedComponents.delete(component);
      componentCleanups.delete(cleanup);
    };

    componentCleanups.add(cleanup);
    component.on('component:remove', cleanup);
  };

  /**
   * Reads the component's current rect and moves its add-element buttons to match.
   * Skips when the element is detached or has a zero rect: during a reorder the moved
   * node is transiently detached and its getBoundingClientRect() reads all-zeros, which
   * would snap the buttons to the canvas top-left (0,0).
   */
  const positionButtonsForComponent = (component: Component): void => {
    const element = component.getEl();
    if (!element || !component.get(EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS) || !attachedComponents.has(component)) {
      return;
    }
    const canvasBody = editor.Canvas.getBody();
    if (!canvasBody) {
      return;
    }
    const rect = element.getBoundingClientRect();
    if (!element.isConnected || (rect.width === 0 && rect.height === 0)) {
      return;
    }
    const offset = getElementOffsetInUnscaledCanvasCoords(element, canvasBody, rect);
    if (!isFiniteCanvasOffset(offset)) {
      return;
    }
    const elementId = element.id || component.getId();
    canvasBody.querySelectorAll(`[data-target-element-id="${elementId}"]`).forEach((btn) => {
      if (btn instanceof HTMLElement) {
        positionButton(btn, offset);
      }
    });
  };

  const repositionButtons = (component: Component): void => {
    // Defer to the next frame so the moved node has re-attached at its new, valid rect.
    const frameId = window.requestAnimationFrame(() => {
      pendingRepositionFrames.delete(frameId);
      positionButtonsForComponent(component);
    });
    pendingRepositionFrames.add(frameId);
  };

  const onComponentMount = (component: Component): void => {
    const element = component.getEl();
    if (element instanceof HTMLElement && component.get(EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS)) {
      attachAddElementButtons(element, component);
    }
  };

  /**
   * The initial canvas render fires `component:mount` before the Canvas frame is wired up, so
   * `editor.Canvas.getBody()` is still null and attach bails for every already-loaded component —
   * and those components never re-mount on their own. Sweep them once the editor has loaded (the
   * canvas is ready by then); attach's `attachedComponents` guard makes it a no-op for anything
   * already attached (e.g. components added later in the load sequence).
   */
  const attachButtonsToLoadedComponents = (): void => {
    const wrapper = editor.getWrapper();
    if (!wrapper) {
      return;
    }
    getAllComponentsInTree(wrapper).forEach((component) => {
      onComponentMount(component);
    });
  };

  editor.on('component:mount', onComponentMount);
  editor.on('component:update', repositionButtons);
  editor.on('component:update:position', repositionButtons);
  editor.on('load', attachButtonsToLoadedComponents);

  return (): void => {
    editor.off('component:mount', onComponentMount);
    editor.off('component:update', repositionButtons);
    editor.off('component:update:position', repositionButtons);
    editor.off('load', attachButtonsToLoadedComponents);
    pendingRepositionFrames.forEach((frameId) => {
      window.cancelAnimationFrame(frameId);
    });
    pendingRepositionFrames.clear();
    componentCleanups.forEach((cleanup) => {
      cleanup();
    });
    unmountAndRemoveStaleMenuContainer();
  };
}
